// Validates configuration at boot time to prevent misconfigured telemetry endpoints
// from leaking data or failing silently. Runs before any error reporting or web vitals initialization.

interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

const isHttpsUrl = (url: string): boolean => {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
};

const isSameOriginTelemetryPath = (url: string): boolean => {
  return url.startsWith('/telemetry/');
};

const isAllowedTelemetryEndpoint = (url: string): boolean => {
  return isSameOriginTelemetryPath(url) || isHttpsUrl(url);
};

export const validateErrorReportingConfig = (): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  const isEnabled =
    import.meta.env.PROD &&
    import.meta.env.VITE_GCP_ERROR_REPORTING_ENABLED !== 'false';

  if (!isEnabled) {
    return { valid: true, errors, warnings };
  }

  const endpoint =
    import.meta.env.VITE_CLIENT_ERRORS_ENDPOINT?.trim() ||
    '/telemetry/client-errors';

  if (!isAllowedTelemetryEndpoint(endpoint)) {
    errors.push(
      `VITE_CLIENT_ERRORS_ENDPOINT must be same-origin /telemetry/* or HTTPS: ${endpoint}`
    );
  }

  return { valid: errors.length === 0, errors, warnings };
};

export const validateWebVitalsConfig = (): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  const endpoint = import.meta.env.VITE_WEB_VITALS_ENDPOINT?.trim();

  // Web vitals endpoint is optional.
  if (!endpoint) {
    return { valid: true, errors, warnings };
  }

  if (!isAllowedTelemetryEndpoint(endpoint)) {
    errors.push(
      `VITE_WEB_VITALS_ENDPOINT must be same-origin /telemetry/* or HTTPS: ${endpoint}`
    );
  }

  return { valid: errors.length === 0, errors, warnings };
};

export const validateApiTelemetryConfig = (): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  const endpoint = import.meta.env.VITE_API_TELEMETRY_ENDPOINT?.trim();

  if (!endpoint) {
    return { valid: true, errors, warnings };
  }

  if (!isAllowedTelemetryEndpoint(endpoint)) {
    errors.push(
      `VITE_API_TELEMETRY_ENDPOINT must be same-origin /telemetry/* or HTTPS: ${endpoint}`
    );
  }

  return { valid: errors.length === 0, errors, warnings };
};

export const validateAllConfig = (): ValidationResult => {
  const results = [
    validateErrorReportingConfig(),
    validateWebVitalsConfig(),
    validateApiTelemetryConfig(),
  ];

  const allErrors = results.flatMap((r) => r.errors);
  const allWarnings = results.flatMap((r) => r.warnings);

  return {
    valid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings,
  };
};

export const initializeConfigValidation = (): void => {
  const validation = validateAllConfig();

  if (!validation.valid) {
    const message = [
      'Configuration errors (telemetry disabled):',
      ...validation.errors.map((e) => `  ✗ ${e}`),
    ].join('\n');

    if (import.meta.env.PROD) {
      // In production, hard fail to prevent misconfigured endpoints.
      console.error(message);
      throw new Error('Telemetry configuration invalid');
    } else {
      // In development, warn but continue (easier iteration).
      console.warn(message);
    }
  }

  if (validation.warnings.length > 0) {
    const message = [
      'Configuration warnings:',
      ...validation.warnings.map((w) => `  ⚠ ${w}`),
    ].join('\n');
    console.warn(message);
  }

  if (import.meta.env.DEV && validation.valid) {
    console.info('[config] Telemetry configuration valid');
  }
};
